import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { getOperator, saveOperator } from '../utils/storage';
import { CATEGORIES, scoreUnit, getTier } from '../utils/scoring';

const CONTRACT_STATUSES = ['prospect', 'outreach', 'negotiation', 'sent', 'signed', 'active', 'expired', 'churned'];
const NOTE_TYPES = ['general', 'call', 'meeting', 'email', 'activation', 'compliance', 'follow-up'];

const ACTIVATION_TYPES = {
  'Education & Training': [
    'Staff Education Session', 'Certification Program', 'Masterclass', 'Bartender Training', 'Sommelier Workshop', 'Brand Ambassador Visit',
  ],
  'Consumer Marketing': [
    'Consumer Tasting Event', 'Seasonal Menu Feature', 'Limited-Time Offering', 'Recipe Development', 'Menu Photography / Content', 'Digital Marketing Campaign',
  ],
  'Experiential & Events': [
    'Pairing Dinner', 'Guest Experience Activation', 'Themed Programming', 'Holiday / Seasonal Activation', 'Community Engagement Event', 'Wellness / Non-Alcoholic Feature',
  ],
  'Creative & Content': [
    'Menu Design / Collateral', 'Photography / Video Production', 'Social Media Content Creation', 'POS Material Development', 'Brand Storytelling Integration',
  ],
  'Analytics & Strategy': [
    'Category Review / Analysis', 'Beverage Program Consultation', 'Performance Reporting', 'Menu Engineering Session',
  ],
};

export default function OperatorDetail() {
  const { operatorId } = useParams();
  const navigate = useNavigate();
  const [op, setOp] = useState(null);
  const [notes, setNotes] = useState([]);
  const [compliance, setCompliance] = useState([]);
  const [activations, setActivations] = useState([]);
  const [spend, setSpend] = useState([]);
  const [packages, setPackages] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState('general');
  const [activeTab, setActiveTab] = useState('overview');
  const [newContact, setNewContact] = useState({ name: '', role: '', email: '', phone: '' });
  const [loading, setLoading] = useState(true);
  const [budgetUnit, setBudgetUnit] = useState('');

  // New activation fields
  const [newAct, setNewAct] = useState({ name: '', type: '', categories: [], unit: '', date: '', status: 'planned', notes: '' });

  // Compliance upload
  const [compUnit, setCompUnit] = useState('');
  const [compType, setCompType] = useState('menu');
  const [compNotes, setCompNotes] = useState('');

  // Spend entry
  const [newSpend, setNewSpend] = useState({ scope: 'unit', category: 'spirits', type: 'Activation / Event', amount: '', description: '', date: new Date().toISOString().split('T')[0] });
  const SPEND_TYPES = ['Activation / Event', 'Education / Training', 'Menu Design / Printing', 'Photography / Content', 'POS Material', 'Digital Marketing', 'Consulting / Strategy', 'Recipe Development', 'Merchandise / Collateral', 'Analytics / Reporting', 'Other'];
  const SPEND_SCOPES = [
    { id: 'unit', label: 'This unit — one category' },
    { id: 'unit_all', label: 'This unit — all categories' },
    { id: 'all_units', label: 'All units — pick category' },
    { id: 'all_all', label: 'All units — all categories' },
  ];

  useEffect(() => { loadAll(); }, [operatorId]);

  const loadAll = async () => {
    const [opData, notesRes, compRes, actRes, spendRes, pkgRes] = await Promise.all([
      getOperator(operatorId),
      supabase.from('bc_notes').select('*').eq('operator_id', operatorId).order('created_at', { ascending: false }),
      supabase.from('bc_compliance').select('*').eq('operator_id', operatorId).order('updated_at', { ascending: false }),
      supabase.from('bc_activations').select('*').eq('operator_id', operatorId).order('created_at', { ascending: false }),
      supabase.from('bc_spend').select('*').eq('operator_id', operatorId).order('date', { ascending: false }),
      supabase.from('bc_packages').select('*'),
    ]);
    setOp(opData);
    setNotes(notesRes.data || []);
    setCompliance(compRes.data || []);
    setActivations(actRes.data || []);
    setSpend(spendRes.data || []);
    setPackages(pkgRes.data || []);
    setLoading(false);
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    const { data: { user } } = await supabase.auth.getUser();
    await supabase.from('bc_notes').insert({ operator_id: operatorId, note: newNote, note_type: noteType, created_by: user?.email || 'admin' });
    setNewNote('');
    loadAll();
  };

  const deleteNote = async (id) => {
    await supabase.from('bc_notes').delete().eq('id', id);
    setNotes(notes.filter(n => n.id !== id));
  };

  const updateContract = async (status) => {
    await supabase.from('bc_operators').update({ contract_status: status }).eq('id', operatorId);
    setOp({ ...op, contract_status: status });
  };

  const addContact = async () => {
    if (!newContact.name) return;
    const contacts = [...(op.contacts || []), { ...newContact, id: Date.now() }];
    await supabase.from('bc_operators').update({ contacts }).eq('id', operatorId);
    setOp({ ...op, contacts });
    setNewContact({ name: '', role: '', email: '', phone: '' });
  };

  const removeContact = async (contactId) => {
    const contacts = (op.contacts || []).filter(c => c.id !== contactId);
    await supabase.from('bc_operators').update({ contacts }).eq('id', operatorId);
    setOp({ ...op, contacts });
  };

  const addActivation = async () => {
    if (!newAct.name) return;
    await supabase.from('bc_activations').insert({ operator_id: operatorId, data: newAct });
    setNewAct({ name: '', type: '', categories: [], unit: '', date: '', status: 'planned', notes: '' });
    loadAll();
  };

  const updateActivation = async (id, field, value) => {
    const act = activations.find(a => a.id === id);
    if (!act) return;
    const updated = { ...act.data, [field]: value };
    await supabase.from('bc_activations').update({ data: updated, updated_at: new Date().toISOString() }).eq('id', id);
    setActivations(activations.map(a => a.id === id ? { ...a, data: updated } : a));
  };

  const addCompliance = async () => {
    if (!compUnit) return;
    await supabase.from('bc_compliance').insert({ operator_id: operatorId, unit_name: compUnit, data: { type: compType, notes: compNotes, status: 'submitted', submittedAt: new Date().toISOString() } });
    setCompNotes('');
    loadAll();
  };

  const updateCompStatus = async (id, status) => {
    const comp = compliance.find(c => c.id === id);
    await supabase.from('bc_compliance').update({ data: { ...comp.data, status, reviewedAt: new Date().toISOString() } }).eq('id', id);
    setCompliance(compliance.map(c => c.id === id ? { ...c, data: { ...c.data, status } } : c));
  };

  if (loading) return <div className="py-20 text-center text-neutral-500">Loading...</div>;
  if (!op) return <div className="py-20 text-center text-neutral-500">Operator not found</div>;

  const units = op.data?.units || [];
  const scored = units.filter(u => +u.totalSales > 0).map(u => ({ ...u, results: scoreUnit(u) })).filter(u => u.results);
  const allResults = scored.flatMap(u => u.results);
  const portfolioScore = allResults.length > 0 ? Math.round(allResults.reduce((s, r) => s + r.finalScore, 0) / allResults.length) : null;
  const portfolioTier = portfolioScore ? getTier(portfolioScore) : null;

  // Spend helpers
  const addSpend = async () => {
    if (!newSpend.unit || !newSpend.amount) return;
    const { data: { user } } = await supabase.auth.getUser();
    await supabase.from('bc_spend').insert({ operator_id: operatorId, unit_name: newSpend.unit, category: newSpend.category, spend_type: newSpend.type, amount: +newSpend.amount, description: newSpend.description, date: newSpend.date, created_by: user?.email || 'admin' });
    setNewSpend({ ...newSpend, amount: '', description: '' });
    loadAll();
  };

  const deleteSpend = async (id) => {
    await supabase.from('bc_spend').delete().eq('id', id);
    setSpend(spend.filter(s => s.id !== id));
  };

  // Get monthly budget for a unit/category based on tier
  const getMonthlyBudget = (unitName, catId) => {
    const unit = units.find(u => u.name === unitName);
    if (!unit) return 0;
    const results = scoreUnit(unit);
    if (!results) return 0;
    const catResult = results.find(r => r.id === catId);
    if (!catResult) return 0;
    const pkg = packages.find(p => p.category === catId && p.tier === catResult.tier.num);
    return pkg?.monthly_total || 0;
  };

  const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'budget', label: 'Budget' },
    { id: 'notes', label: `Notes (${notes.length})` },
    { id: 'contacts', label: `Contacts (${(op.contacts || []).length})` },
    { id: 'compliance', label: `Compliance (${compliance.length})` },
    { id: 'activations', label: `Activations (${activations.length})` },
  ];

  const statusColor = { prospect: 'text-neutral-500', outreach: 'text-blue-400', negotiation: 'text-amber-400', sent: 'text-purple-400', signed: 'text-emerald-400', active: 'text-emerald-400', expired: 'text-red-400', churned: 'text-red-400' };

  return (
    <div className="py-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/')} className="btn-ghost text-xs">← Back</button>
          {portfolioTier && (
            <div className="w-12 h-12 rounded-xl flex flex-col items-center justify-center" style={{ backgroundColor: portfolioTier.color + '15' }}>
              <span className="text-lg font-black" style={{ color: portfolioTier.color }}>{portfolioScore}</span>
            </div>
          )}
          <div>
            <h1 className="font-display text-xl text-white">{op.operator_name || 'Operator'}</h1>
            <p className="text-[10px] text-neutral-500">{units.length} unit(s)</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select value={op.contract_status || 'prospect'} onChange={e => updateContract(e.target.value)}
            className={`text-xs px-3 py-2 rounded-lg font-medium border-0 bg-neutral-800 ${statusColor[op.contract_status] || 'text-neutral-400'}`}>
            {CONTRACT_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
          <Link to={`/calculator/${operatorId}`} className="btn-secondary text-xs py-2">Edit Scoring</Link>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 overflow-x-auto scrollbar-hide">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`whitespace-nowrap px-4 py-2 rounded-lg text-xs font-medium transition-all ${activeTab === tab.id ? 'bg-brand-gold/15 text-brand-gold' : 'text-neutral-500 hover:text-neutral-300'}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview */}
      {activeTab === 'overview' && scored.length > 0 && (
        <div className="card p-5">
          <h2 className="text-sm font-semibold text-brand-gold mb-4">Category Tiers</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {scored[0]?.results?.map(r => (
              <div key={r.id} className="bg-neutral-800/30 rounded-xl p-4 text-center" style={{ borderTop: `3px solid ${r.tier.color}` }}>
                <p className="text-[10px] text-neutral-500 uppercase">{r.icon} {r.name}</p>
                <p className="text-3xl font-black mt-1" style={{ color: r.tier.color }}>T{r.tier.num}</p>
                <p className="text-xs font-semibold" style={{ color: r.tier.color }}>{r.tier.name}</p>
                <p className="text-[10px] text-neutral-600 mt-1">{r.finalScore}/100</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notes / CRM */}
      {activeTab === 'notes' && (
        <div className="card p-5">
          <h2 className="text-sm font-semibold text-brand-gold mb-4">Activity Notes</h2>
          <div className="flex gap-2 mb-4">
            <select value={noteType} onChange={e => setNoteType(e.target.value)} className="input-field text-xs py-2 w-28">
              {NOTE_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
            </select>
            <input value={newNote} onChange={e => setNewNote(e.target.value)} onKeyDown={e => e.key === 'Enter' && addNote()}
              placeholder="Add a note..." className="input-field text-sm py-2 flex-1" />
            <button onClick={addNote} className="btn-primary text-xs py-2">Add</button>
          </div>
          <div className="space-y-2">
            {notes.map(n => (
              <div key={n.id} className="bg-neutral-800/20 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[9px] px-2 py-0.5 rounded bg-neutral-800 text-neutral-500">{n.note_type}</span>
                  <span className="text-[10px] text-neutral-600">{n.created_by}</span>
                  <span className="text-[10px] text-neutral-700">{new Date(n.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                  <button onClick={() => deleteNote(n.id)} className="ml-auto text-neutral-700 hover:text-red-400 text-[10px]">Delete</button>
                </div>
                <p className="text-xs text-neutral-300">{n.note}</p>
              </div>
            ))}
            {notes.length === 0 && <p className="text-neutral-600 text-xs text-center py-4">No notes yet.</p>}
          </div>
        </div>
      )}

      {/* Contacts */}
      {activeTab === 'contacts' && (
        <div className="card p-5">
          <h2 className="text-sm font-semibold text-brand-gold mb-4">Key Contacts</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
            <input value={newContact.name} onChange={e => setNewContact({ ...newContact, name: e.target.value })} placeholder="Name" className="input-field text-xs py-2" />
            <input value={newContact.role} onChange={e => setNewContact({ ...newContact, role: e.target.value })} placeholder="Role / Title" className="input-field text-xs py-2" />
            <input value={newContact.email} onChange={e => setNewContact({ ...newContact, email: e.target.value })} placeholder="Email" className="input-field text-xs py-2" />
            <div className="flex gap-1">
              <input value={newContact.phone} onChange={e => setNewContact({ ...newContact, phone: e.target.value })} placeholder="Phone" className="input-field text-xs py-2 flex-1" />
              <button onClick={addContact} className="btn-primary text-xs py-2">Add</button>
            </div>
          </div>
          <div className="space-y-2">
            {(op.contacts || []).map(c => (
              <div key={c.id} className="flex items-center gap-3 bg-neutral-800/20 rounded-lg p-3">
                <div className="flex-1">
                  <p className="text-sm font-medium text-white">{c.name}</p>
                  <p className="text-[10px] text-neutral-500">{c.role}{c.email ? ` · ${c.email}` : ''}{c.phone ? ` · ${c.phone}` : ''}</p>
                </div>
                <button onClick={() => removeContact(c.id)} className="text-neutral-700 hover:text-red-400 text-[10px]">Remove</button>
              </div>
            ))}
            {(op.contacts || []).length === 0 && <p className="text-neutral-600 text-xs text-center py-4">No contacts added.</p>}
          </div>
        </div>
      )}

      {/* Compliance */}
      {activeTab === 'compliance' && (
        <div className="card p-5">
          <h2 className="text-sm font-semibold text-brand-gold mb-4">Compliance Tracking</h2>
          <div className="flex flex-wrap gap-2 mb-4">
            <select value={compUnit} onChange={e => setCompUnit(e.target.value)} className="input-field text-xs py-2 w-40">
              <option value="">Select Unit...</option>
              {units.map((u, i) => <option key={i} value={u.name}>{u.name}</option>)}
            </select>
            <select value={compType} onChange={e => setCompType(e.target.value)} className="input-field text-xs py-2 w-32">
              <option value="menu">Menu Upload</option>
              <option value="pmix">P-Mix Report</option>
              <option value="activation">Activation Proof</option>
              <option value="education">Education Cert</option>
              <option value="other">Other</option>
            </select>
            <input value={compNotes} onChange={e => setCompNotes(e.target.value)} placeholder="Notes..." className="input-field text-xs py-2 flex-1" />
            <button onClick={addCompliance} className="btn-primary text-xs py-2">Submit</button>
          </div>
          <div className="space-y-2">
            {compliance.map(c => (
              <div key={c.id} className="flex items-center gap-3 bg-neutral-800/20 rounded-lg p-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-medium text-white">{c.unit_name}</span>
                    <span className="text-[9px] px-2 py-0.5 rounded bg-neutral-800 text-neutral-500">{c.data?.type}</span>
                    <span className={`text-[9px] px-2 py-0.5 rounded font-medium ${c.data?.status === 'approved' ? 'bg-emerald-500/15 text-emerald-400' : c.data?.status === 'rejected' ? 'bg-red-500/15 text-red-400' : 'bg-amber-500/15 text-amber-400'}`}>
                      {c.data?.status || 'submitted'}
                    </span>
                  </div>
                  {c.data?.notes && <p className="text-[10px] text-neutral-500">{c.data.notes}</p>}
                  <p className="text-[9px] text-neutral-700">{new Date(c.updated_at).toLocaleDateString()}</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => updateCompStatus(c.id, 'approved')} className="text-[9px] px-2 py-1 rounded bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20">Approve</button>
                  <button onClick={() => updateCompStatus(c.id, 'rejected')} className="text-[9px] px-2 py-1 rounded bg-red-500/10 text-red-400 hover:bg-red-500/20">Reject</button>
                </div>
              </div>
            ))}
            {compliance.length === 0 && <p className="text-neutral-600 text-xs text-center py-4">No compliance records.</p>}
          </div>
        </div>
      )}

      {/* Activations */}
      {activeTab === 'activations' && (
        <div className="card p-5">
          <h2 className="text-sm font-semibold text-brand-gold mb-4">Activation Calendar</h2>
          <div className="bg-neutral-800/20 rounded-lg p-4 mb-4">
            <p className="text-[10px] text-neutral-500 mb-3 uppercase tracking-wider">New Activation</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
              <select value={newAct.unit} onChange={e => setNewAct({ ...newAct, unit: e.target.value })} className="input-field text-xs py-2">
                <option value="">Select Unit...</option>
                {units.map((u, i) => <option key={i} value={u.name}>{u.name}</option>)}
              </select>
              <select value={newAct.type} onChange={e => setNewAct({ ...newAct, type: e.target.value, name: e.target.value })} className="input-field text-xs py-2">
                <option value="">Activation Type...</option>
                {Object.entries(ACTIVATION_TYPES).map(([group, types]) => (
                  <optgroup key={group} label={group}>
                    {types.map(t => <option key={t} value={t}>{t}</option>)}
                  </optgroup>
                ))}
              </select>
              <div className="flex items-center gap-2 bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2">
                {CATEGORIES.map(c => (
                  <label key={c.id} className="flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={newAct.categories.includes(c.id)}
                      onChange={e => {
                        const cats = e.target.checked ? [...newAct.categories, c.id] : newAct.categories.filter(id => id !== c.id);
                        setNewAct({ ...newAct, categories: cats });
                      }}
                      className="rounded border-neutral-600 bg-neutral-800 text-brand-gold focus:ring-brand-gold/30 w-3 h-3" />
                    <span className="text-[10px] text-neutral-400">{c.icon}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <input type="date" value={newAct.date} onChange={e => setNewAct({ ...newAct, date: e.target.value })} className="input-field text-xs py-2" />
              <input value={newAct.notes} onChange={e => setNewAct({ ...newAct, notes: e.target.value })} placeholder="Notes" className="input-field text-xs py-2" />
              <button onClick={addActivation} disabled={!newAct.type || !newAct.unit} className="btn-primary text-xs py-2 disabled:opacity-40">Add Activation</button>
            </div>
          </div>
          <div className="space-y-2">
            {activations.map(a => {
              const cats = Array.isArray(a.data?.categories) ? a.data.categories : (a.data?.category ? [a.data.category] : []);
              const catIcons = cats.map(cid => CATEGORIES.find(c => c.id === cid)?.icon).filter(Boolean).join(' ') || '📋';
              return (
                <div key={a.id} className="flex items-center gap-3 bg-neutral-800/20 rounded-lg p-3">
                  <span className="text-lg">{catIcons}</span>
                  <div className="flex-1">
                    <p className="text-xs font-medium text-white">{a.data?.name || a.data?.type}</p>
                    <p className="text-[10px] text-neutral-500">
                      {a.data?.unit ? `${a.data.unit} · ` : ''}
                      {a.data?.date ? new Date(a.data.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'No date'}
                      {a.data?.notes ? ` · ${a.data.notes}` : ''}
                    </p>
                  </div>
                  <select value={a.data?.status || 'planned'} onChange={e => updateActivation(a.id, 'status', e.target.value)}
                    className={`text-[10px] px-2 py-1 rounded font-medium border-0 ${
                      a.data?.status === 'completed' ? 'bg-emerald-500/15 text-emerald-400' :
                      a.data?.status === 'active' ? 'bg-blue-500/15 text-blue-400' :
                      a.data?.status === 'cancelled' ? 'bg-red-500/15 text-red-400' :
                      'bg-amber-500/15 text-amber-400'
                    }`}>
                    <option value="planned">Planned</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              );
            })}
            {activations.length === 0 && <p className="text-neutral-600 text-xs text-center py-4">No activations planned.</p>}

            {/* Mini calendar view */}
            {activations.length > 0 && (() => {
              const now = new Date();
              const month = now.getMonth();
              const year = now.getFullYear();
              const firstDay = new Date(year, month, 1);
              const lastDay = new Date(year, month + 1, 0);
              const startPad = (firstDay.getDay() + 6) % 7;
              const days = [];
              for (let i = -startPad; i <= lastDay.getDate() + (6 - (lastDay.getDay() + 6) % 7); i++) {
                const date = new Date(year, month, i + 1);
                const dateStr = date.toISOString().split('T')[0];
                const dayActs = activations.filter(a => a.data?.date === dateStr);
                days.push({ date, dateStr, day: date.getDate(), inMonth: date.getMonth() === month, activations: dayActs });
              }
              const weeks = [];
              for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
              const DAYS_OF_WEEK = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

              return (
                <div className="mt-4 bg-neutral-800/20 rounded-lg p-4">
                  <h3 className="text-xs font-semibold text-neutral-400 mb-3">{['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][month]} {year}</h3>
                  <div className="grid grid-cols-7 gap-1 mb-1">
                    {DAYS_OF_WEEK.map(d => <div key={d} className="text-[8px] text-neutral-600 text-center">{d}</div>)}
                  </div>
                  {weeks.map((week, wi) => (
                    <div key={wi} className="grid grid-cols-7 gap-1">
                      {week.map((day, di) => (
                        <div key={di} className={`min-h-[48px] rounded p-1 ${day.inMonth ? 'bg-neutral-800/40' : 'opacity-30'}`}>
                          <p className={`text-[8px] ${day.dateStr === new Date().toISOString().split('T')[0] ? 'text-brand-gold font-bold' : 'text-neutral-600'}`}>{day.day}</p>
                          {day.activations.map((a, ai) => {
                            const cats = Array.isArray(a.data?.categories) ? a.data.categories : [];
                            const icon = cats.map(cid => CATEGORIES.find(c => c.id === cid)?.icon).filter(Boolean).join('') || '📋';
                            return <div key={ai} className="text-[6px] px-0.5 py-0.5 rounded bg-brand-gold/10 text-brand-gold truncate mt-0.5">{icon} {a.data?.type?.substring(0, 12)}</div>;
                          })}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Budget */}
      {activeTab === 'budget' && (
        <div>
          {/* Unit selector */}
          <div className="flex gap-2 mb-4 flex-wrap">
            {units.map((u, i) => (
              <button key={i} onClick={() => setBudgetUnit(u.name)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${budgetUnit === u.name ? 'bg-brand-gold/15 text-brand-gold border border-brand-gold/40' : 'bg-neutral-800/40 text-neutral-500 border border-neutral-800'}`}>
                {u.name}
              </button>
            ))}
            {!budgetUnit && units.length > 0 && setBudgetUnit(units[0].name) === undefined && null}
          </div>

          {budgetUnit && (() => {
            const unitSpend = spend.filter(s => s.unit_name === budgetUnit);
            const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM
            const thisMonthSpend = unitSpend.filter(s => s.date?.startsWith(currentMonth));

            return (
              <>
                {/* Category budgets */}
                <div className="card p-5 mb-4">
                  <h2 className="text-sm font-semibold text-brand-gold mb-4">{budgetUnit} — Monthly Budget</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {CATEGORIES.map(cat => {
                      const budget = getMonthlyBudget(budgetUnit, cat.id);
                      const spent = thisMonthSpend.filter(s => s.category === cat.id).reduce((sum, s) => sum + (+s.amount || 0), 0);
                      const remaining = budget - spent;
                      const pct = budget > 0 ? Math.min(100, (spent / budget) * 100) : 0;
                      return (
                        <div key={cat.id} className="bg-neutral-800/30 rounded-xl p-4">
                          <p className="text-[10px] text-neutral-500 uppercase mb-1">{cat.icon} {cat.name}</p>
                          <p className="text-lg font-black text-white">${Math.round(budget).toLocaleString()}</p>
                          <p className="text-[9px] text-neutral-600 mb-2">monthly budget</p>
                          <div className="h-2 bg-neutral-800 rounded-full overflow-hidden mb-1">
                            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: pct > 90 ? '#f87171' : pct > 70 ? '#fbbf24' : '#34d399' }} />
                          </div>
                          <div className="flex justify-between text-[9px]">
                            <span className="text-neutral-500">Spent: <span className="text-white">${Math.round(spent).toLocaleString()}</span></span>
                            <span className={remaining < 0 ? 'text-red-400' : 'text-emerald-400'}>${Math.round(remaining).toLocaleString()} left</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Add spend */}
                <div className="card p-5 mb-4">
                  <h2 className="text-sm font-semibold text-brand-gold mb-3">Log Spend</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
                    <select value={newSpend.scope} onChange={e => setNewSpend({ ...newSpend, scope: e.target.value })} className="input-field text-xs py-2">
                      {SPEND_SCOPES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                    </select>
                    {(newSpend.scope === 'unit' || newSpend.scope === 'all_units') && (
                      <select value={newSpend.category} onChange={e => setNewSpend({ ...newSpend, category: e.target.value })} className="input-field text-xs py-2">
                        {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                      </select>
                    )}
                    <select value={newSpend.type} onChange={e => setNewSpend({ ...newSpend, type: e.target.value })} className="input-field text-xs py-2">
                      {SPEND_TYPES.map(t => <option key={t}>{t}</option>)}
                    </select>
                    <input type="number" value={newSpend.amount} onChange={e => setNewSpend({ ...newSpend, amount: e.target.value })} placeholder="Total amount ($)" className="input-field text-xs py-2" />
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <input type="date" value={newSpend.date} onChange={e => setNewSpend({ ...newSpend, date: e.target.value })} className="input-field text-xs py-2" />
                    <input value={newSpend.description} onChange={e => setNewSpend({ ...newSpend, description: e.target.value })} placeholder="Description" className="input-field text-xs py-2" />
                    <button onClick={async () => {
                      if (!newSpend.amount) return;
                      const { data: { user } } = await supabase.auth.getUser();
                      const amount = +newSpend.amount;
                      const entries = [];
                      const scope = newSpend.scope;

                      // Determine target units
                      const targetUnits = (scope === 'all_units' || scope === 'all_all') ? units.map(u => u.name) : [budgetUnit];
                      // Determine target categories
                      const targetCats = (scope === 'unit_all' || scope === 'all_all') ? CATEGORIES.map(c => c.id) : [newSpend.category];

                      const totalSplits = targetUnits.length * targetCats.length;
                      const splitAmount = Math.round((amount / totalSplits) * 100) / 100;

                      targetUnits.forEach(unitName => {
                        targetCats.forEach(catId => {
                          entries.push({
                            operator_id: operatorId, unit_name: unitName, category: catId,
                            spend_type: newSpend.type, amount: splitAmount,
                            description: `${newSpend.description || ''}${totalSplits > 1 ? ` (split ${totalSplits} ways from ${fmt(amount)})` : ''}`,
                            date: newSpend.date, created_by: user?.email || 'admin',
                          });
                        });
                      });

                      await supabase.from('bc_spend').insert(entries);
                      setNewSpend({ ...newSpend, amount: '', description: '' });
                      loadAll();
                    }} disabled={!newSpend.amount}
                      className="btn-primary text-xs py-2 disabled:opacity-40">Log Spend</button>
                  </div>
                  {(newSpend.scope !== 'unit' && newSpend.amount) && (
                    <p className="text-[9px] text-neutral-600 mt-2">
                      {fmt(+newSpend.amount)} will be split across {newSpend.scope === 'unit_all' ? `${CATEGORIES.length} categories at ${budgetUnit}` : newSpend.scope === 'all_units' ? `${units.length} units in ${CATEGORIES.find(c => c.id === newSpend.category)?.name}` : `${units.length} units × ${CATEGORIES.length} categories`}
                      {' = '}{fmt(+newSpend.amount / ((newSpend.scope === 'unit_all' ? CATEGORIES.length : newSpend.scope === 'all_units' ? units.length : units.length * CATEGORIES.length) || 1))} each
                    </p>
                  )}
                </div>

                {/* Spend history */}
                <div className="card p-5">
                  <h2 className="text-sm font-semibold text-brand-gold mb-3">Spend History — {budgetUnit}</h2>
                  {unitSpend.length > 0 ? (
                    <div className="space-y-2">
                      {unitSpend.map(s => {
                        const cat = CATEGORIES.find(c => c.id === s.category);
                        return (
                          <div key={s.id} className="flex items-center gap-3 bg-neutral-800/20 rounded-lg p-3">
                            <span className="text-base">{cat?.icon || '📋'}</span>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className="text-xs font-medium text-white">{s.spend_type}</span>
                                <span className="text-[9px] px-2 py-0.5 rounded bg-neutral-800 text-neutral-500">{cat?.name}</span>
                              </div>
                              {s.description && <p className="text-[10px] text-neutral-500">{s.description}</p>}
                              <p className="text-[9px] text-neutral-700">{s.date} · {s.created_by}</p>
                            </div>
                            <p className="text-sm font-bold text-white">${(+s.amount).toLocaleString()}</p>
                            <button onClick={() => deleteSpend(s.id)} className="text-neutral-700 hover:text-red-400 text-[10px]">×</button>
                          </div>
                        );
                      })}
                    </div>
                  ) : <p className="text-neutral-600 text-xs text-center py-4">No spend logged yet.</p>}
                </div>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}
